import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { 
  Users, GraduationCap, UserPlus, LogOut, Layers, BookOpen, 
  Plus, Trash2, ToggleLeft, ToggleRight, Search, Pencil, KeyRound,
  Building2, FileEdit, Settings, FileText, Clock, CheckCircle, Calendar, BarChart3,
  Crown, Star, Sparkles, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const SchoolAdminDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [activeTab, setActiveTab] = useState('teachers');
  const [school, setSchool] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [dataEntryOperators, setDataEntryOperators] = useState([]);
  const [exams, setExams] = useState([]);
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showSchoolSettingsModal, setShowSchoolSettingsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [createUserType, setCreateUserType] = useState('teacher');
  const [searchTerm, setSearchTerm] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'teacher',
    board_id: '',
    grade_id: '',
    subject_ids: [],
    board_ids: [],
    grade_ids: [],
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    board_id: '',
    grade_id: '',
    subject_ids: [],
    board_ids: [],
    grade_ids: [],
  });

  const [schoolSettings, setSchoolSettings] = useState({
    name: '',
    logo_url: ''
  });

  // Subscription tier state
  const [subscription, setSubscription] = useState({
    tier: 'starter',
    globalQuestionBank: false,
    bulkImport: false,
    aoAnalytics: false,
    aiGrading: false
  });

  const fetchData = async () => {
    try {
      const [schoolRes, teachersRes, studentsRes, operatorsRes, examsRes, boardsRes, gradesRes, subjectsRes, subscriptionRes] = await Promise.all([
        api.get('/school/info'),
        api.get('/school-admin/teachers'),
        api.get('/school-admin/students'),
        api.get('/school-admin/data-entry-operators'),
        api.get('/exams'),
        api.get('/boards'),
        api.get('/grades'),
        api.get('/subjects'),
        api.get('/school-admin/subscription').catch(() => ({ data: { tier: 'starter' } })),
      ]);
      setSchool(schoolRes.data);
      setTeachers(teachersRes.data);
      setStudents(studentsRes.data);
      setDataEntryOperators(operatorsRes.data);
      setExams(examsRes.data);
      setBoards(boardsRes.data);
      setGrades(gradesRes.data);
      setSubjects(subjectsRes.data);
      
      // Set subscription features
      const subData = subscriptionRes.data;
      setSubscription({
        tier: subData.tier || 'starter',
        globalQuestionBank: subData.global_question_bank || false,
        bulkImport: subData.bulk_import || false,
        aoAnalytics: subData.ao_analytics || false,
        aiGrading: subData.ai_grading || false
      });
      
      if (schoolRes.data) {
        setSchoolSettings({
          name: schoolRes.data.name || '',
          logo_url: schoolRes.data.logo_url || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'school_admin') {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const updateExamStatus = async (examId, status) => {
    const loadingToast = toast.loading(`${status === 'active' ? 'Activating' : 'Updating'} exam...`);
    try {
      await api.patch(`/exams/${examId}/status?status=${status}`);
      toast.dismiss(loadingToast);
      toast.success(`Exam ${status === 'active' ? 'activated' : 'updated'} successfully!`);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to update exam status');
    }
  };

  const openCreateModal = (type) => {
    setCreateUserType(type);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: type,
      board_id: '',
      grade_id: '',
      subject_ids: [],
      board_ids: [],
      grade_ids: [],
    });
    setShowCreateModal(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(`Creating ${createUserType}...`);
    
    try {
      await api.post('/school-admin/users', formData);
      toast.dismiss(loadingToast);
      toast.success(`${createUserType} created successfully!`);
      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditFormData({
      name: userToEdit.name || '',
      board_id: userToEdit.board_id || '',
      grade_id: userToEdit.grade_id || '',
      subject_ids: userToEdit.subject_ids || [],
      board_ids: userToEdit.board_ids || [],
      grade_ids: userToEdit.grade_ids || [],
    });
    setShowEditModal(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Updating user...');
    
    try {
      await api.patch(`/school-admin/users/${editingUser.id}`, editFormData);
      toast.dismiss(loadingToast);
      toast.success('User updated successfully!');
      setShowEditModal(false);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const openResetPasswordModal = (userToReset) => {
    setResetPasswordUser(userToReset);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    const loadingToast = toast.loading('Resetting password...');
    
    try {
      await api.patch(`/school-admin/users/${resetPasswordUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.dismiss(loadingToast);
      toast.success(`Password reset for ${resetPasswordUser.name}`);
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const toggleUserActive = async (userId, currentlyActive) => {
    try {
      await api.patch(`/school-admin/users/${userId}/toggle-active`);
      toast.success(currentlyActive ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;
    
    try {
      await api.delete(`/school-admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleSubjectToggle = (subjectId, isEdit = false) => {
    if (isEdit) {
      setEditFormData(prev => {
        const currentIds = prev.subject_ids;
        if (currentIds.includes(subjectId)) {
          return { ...prev, subject_ids: currentIds.filter(id => id !== subjectId) };
        } else {
          return { ...prev, subject_ids: [...currentIds, subjectId] };
        }
      });
    } else {
      setFormData(prev => {
        const currentIds = prev.subject_ids;
        if (currentIds.includes(subjectId)) {
          return { ...prev, subject_ids: currentIds.filter(id => id !== subjectId) };
        } else {
          return { ...prev, subject_ids: [...currentIds, subjectId] };
        }
      });
    }
  };

  const handleMultiSelect = (field, itemId, isEdit = false) => {
    const setter = isEdit ? setEditFormData : setFormData;
    setter(prev => {
      const currentIds = prev[field];
      if (currentIds.includes(itemId)) {
        return { ...prev, [field]: currentIds.filter(id => id !== itemId) };
      } else {
        return { ...prev, [field]: [...currentIds, itemId] };
      }
    });
  };

  const handleUpdateSchoolSettings = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Updating school settings...');
    
    try {
      await api.patch(`/super-admin/schools/${school.id}`, schoolSettings);
      toast.dismiss(loadingToast);
      toast.success('School settings updated!');
      setShowSchoolSettingsModal(false);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    }
  };

  // Board/Grade/Subject management
  const [newBoard, setNewBoard] = useState('');
  const [newGrade, setNewGrade] = useState('');
  const [newSubject, setNewSubject] = useState('');

  const addBoard = async () => {
    if (!newBoard.trim()) return;
    try {
      await api.post('/boards', { name: newBoard.trim() });
      setNewBoard('');
      fetchData();
      toast.success('Board added');
    } catch (err) {
      toast.error('Failed to add board');
    }
  };

  const addGrade = async () => {
    if (!newGrade.trim()) return;
    try {
      await api.post('/grades', { name: newGrade.trim() });
      setNewGrade('');
      fetchData();
      toast.success('Grade added');
    } catch (err) {
      toast.error('Failed to add grade');
    }
  };

  const addSubject = async () => {
    if (!newSubject.trim()) return;
    try {
      await api.post('/subjects', { name: newSubject.trim() });
      setNewSubject('');
      fetchData();
      toast.success('Subject added');
    } catch (err) {
      toast.error('Failed to add subject');
    }
  };

  const deleteBoard = async (id) => {
    if (!window.confirm('Delete this board?')) return;
    try {
      await api.delete(`/boards/${id}`);
      fetchData();
      toast.success('Board deleted');
    } catch (err) {
      toast.error('Failed to delete board');
    }
  };

  const deleteGrade = async (id) => {
    if (!window.confirm('Delete this grade?')) return;
    try {
      await api.delete(`/grades/${id}`);
      fetchData();
      toast.success('Grade deleted');
    } catch (err) {
      toast.error('Failed to delete grade');
    }
  };

  const deleteSubject = async (id) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await api.delete(`/subjects/${id}`);
      fetchData();
      toast.success('Subject deleted');
    } catch (err) {
      toast.error('Failed to delete subject');
    }
  };

  const filteredTeachers = teachers.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOperators = dataEntryOperators.filter(o => 
    o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with School Branding */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {school?.logo_url ? (
              <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-indigo-700" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-900">{school?.name || 'School Admin Dashboard'}</h1>
              <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSchoolSettingsModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
            >
              <Settings className="w-5 h-5" />
              School Settings
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Users className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Teachers</p>
                <p className="text-2xl font-bold text-slate-900">{teachers.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <GraduationCap className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Students</p>
                <p className="text-2xl font-bold text-slate-900">{students.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <FileEdit className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Data Entry</p>
                <p className="text-2xl font-bold text-slate-900">{dataEntryOperators.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Subjects</p>
                <p className="text-2xl font-bold text-slate-900">{subjects.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('teachers')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              activeTab === 'teachers' ? 'bg-indigo-700 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Teachers
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              activeTab === 'students' ? 'bg-indigo-700 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Students
          </button>
          <button
            onClick={() => setActiveTab('data_entry')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              activeTab === 'data_entry' ? 'bg-indigo-700 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Data Entry Operators
          </button>
          <button
            onClick={() => setActiveTab('exams')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              activeTab === 'exams' ? 'bg-indigo-700 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Exams ({exams.filter(e => e.status === 'draft').length} drafts)
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              activeTab === 'settings' ? 'bg-indigo-700 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Boards/Grades/Subjects
          </button>
          <button
            onClick={() => navigate('/school-admin/subjects')}
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-slate-600 hover:bg-slate-100 border border-indigo-200"
            data-testid="igcse-subjects-btn"
          >
            IGCSE Subjects
          </button>
          <button
            onClick={() => navigate('/school-admin/progress-reports')}
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-slate-600 hover:bg-slate-100 border border-emerald-200"
            data-testid="progress-reports-btn"
          >
            Progress Reports
          </button>
          <button
            onClick={() => navigate('/school-admin/student-analytics')}
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-slate-600 hover:bg-slate-100 border border-purple-200"
            data-testid="student-analytics-btn"
          >
            Student Analytics
          </button>
          <button
            onClick={() => navigate('/school-admin/assessments')}
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-slate-600 hover:bg-slate-100 border border-orange-200"
            data-testid="assessments-config-btn"
          >
            Assessments
          </button>
          {/* Bulk Import - Professional and Premium only */}
          {subscription.bulkImport ? (
            <button
              onClick={() => navigate('/school-admin/bulk-import')}
              className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-white bg-green-600 hover:bg-green-700"
              data-testid="bulk-import-btn"
            >
              Bulk Import
            </button>
          ) : (
            <button
              disabled
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-slate-400 bg-slate-100 cursor-not-allowed"
              title="Upgrade to Professional or Premium for Bulk Import"
              data-testid="bulk-import-btn-locked"
            >
              <Lock className="w-4 h-4" />
              Bulk Import
            </button>
          )}
          {/* School Question Bank - Available for all tiers */}
          <button
            onClick={() => navigate('/school-admin/question-bank')}
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-white bg-indigo-600 hover:bg-indigo-700"
            data-testid="question-bank-btn"
          >
            Question Bank
          </button>
          {/* Global Question Bank - Professional and Premium only */}
          {subscription.globalQuestionBank && (
            <button
              onClick={() => navigate('/qbm/dashboard')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-white bg-purple-600 hover:bg-purple-700"
              data-testid="global-qb-btn"
            >
              <Star className="w-4 h-4" />
              10-Year Papers
            </button>
          )}
        </div>

        {/* Subscription Badge */}
        <div className="mb-4">
          {subscription.tier === 'premium' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              <Crown className="w-4 h-4" />
              Premium Plan
            </span>
          )}
          {subscription.tier === 'professional' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
              <Star className="w-4 h-4" />
              Professional Plan
            </span>
          )}
          {subscription.tier === 'starter' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Starter Plan
            </span>
          )}
        </div>

        {/* Search and Actions - Only show for user tabs */}
        {!['settings', 'exams'].includes(activeTab) && (
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-80 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => openCreateModal(
              activeTab === 'teachers' ? 'teacher' : 
              activeTab === 'students' ? 'student' : 'data_entry_operator'
            )}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg transition-all"
          >
            <UserPlus className="w-5 h-5" />
            Create {activeTab === 'teachers' ? 'Teacher' : activeTab === 'students' ? 'Student' : 'Data Entry Operator'}
          </button>
        </div>
        )}

        {/* Settings Tab - Boards/Grades/Subjects */}
        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Boards */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" />
                Boards
              </h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newBoard}
                  onChange={(e) => setNewBoard(e.target.value)}
                  placeholder="New board name"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onKeyPress={(e) => e.key === 'Enter' && addBoard()}
                />
                <button
                  onClick={addBoard}
                  className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {boards.map(board => (
                  <div key={board.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">{board.name}</span>
                    <button
                      onClick={() => deleteBoard(board.id)}
                      className="p-1 text-slate-400 hover:text-red-600 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {boards.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No boards yet</p>
                )}
              </div>
            </div>

            {/* Grades */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                Grades
              </h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newGrade}
                  onChange={(e) => setNewGrade(e.target.value)}
                  placeholder="New grade name"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  onKeyPress={(e) => e.key === 'Enter' && addGrade()}
                />
                <button
                  onClick={addGrade}
                  className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {grades.map(grade => (
                  <div key={grade.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">{grade.name}</span>
                    <button
                      onClick={() => deleteGrade(grade.id)}
                      className="p-1 text-slate-400 hover:text-red-600 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {grades.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No grades yet</p>
                )}
              </div>
            </div>

            {/* Subjects */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-600" />
                Subjects
              </h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder="New subject name"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  onKeyPress={(e) => e.key === 'Enter' && addSubject()}
                />
                <button
                  onClick={addSubject}
                  className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {subjects.map(subject => (
                  <div key={subject.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">{subject.name}</span>
                    <button
                      onClick={() => deleteSubject(subject.id)}
                      className="p-1 text-slate-400 hover:text-red-600 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {subjects.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No subjects yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Users Table - Only show for user tabs */}
        {activeTab !== 'settings' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Email</th>
                {activeTab === 'students' && (
                  <>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Board</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Grade</th>
                  </>
                )}
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                  {activeTab === 'data_entry' ? 'Role' : 'Subjects'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(activeTab === 'teachers' ? filteredTeachers : 
                activeTab === 'students' ? filteredStudents : filteredOperators).map(userItem => (
                <tr key={userItem.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">{userItem.name}</td>
                  <td className="px-6 py-4 text-slate-600">{userItem.email}</td>
                  {activeTab === 'students' && (
                    <>
                      <td className="px-6 py-4 text-slate-600">{userItem.board_name || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{userItem.grade_name || '-'}</td>
                    </>
                  )}
                  <td className="px-6 py-4">
                    {activeTab === 'data_entry' ? (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                        Data Entry
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(userItem.subject_names || []).slice(0, 3).map((name, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                            {name}
                          </span>
                        ))}
                        {(userItem.subject_names || []).length > 3 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                            +{userItem.subject_names.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      userItem.is_active !== false
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {userItem.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEditModal(userItem)}
                        className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openResetPasswordModal(userItem)}
                        className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                        title="Reset Password"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleUserActive(userItem.id, userItem.is_active !== false)}
                        className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                        title={userItem.is_active !== false ? 'Deactivate' : 'Activate'}
                      >
                        {userItem.is_active !== false ? (
                          <ToggleRight className="w-4 h-4" />
                        ) : (
                          <ToggleLeft className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteUser(userItem.id, userItem.name)}
                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(activeTab === 'teachers' ? filteredTeachers : 
                activeTab === 'students' ? filteredStudents : filteredOperators).length === 0 && (
                <tr>
                  <td colSpan={activeTab === 'students' ? 7 : 5} className="px-6 py-12 text-center text-slate-500">
                    No {activeTab === 'teachers' ? 'teachers' : activeTab === 'students' ? 'students' : 'data entry operators'} found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <div className="space-y-6">
            {/* Draft Exams Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Draft Exams Awaiting Activation ({exams.filter(e => e.status === 'draft').length})
              </h3>
              {exams.filter(e => e.status === 'draft').length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
                  No draft exams pending activation
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exams.filter(e => e.status === 'draft').map((exam) => (
                    <div key={exam.id} className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-slate-900">{exam.title}</h4>
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          Draft
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {exam.board_name && (
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                            {exam.board_name}
                          </span>
                        )}
                        {exam.grade_name && (
                          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                            {exam.grade_name}
                          </span>
                        )}
                        {exam.subject_name && (
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                            {exam.subject_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm text-slate-600 space-y-1 mb-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {exam.questions.length} questions
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {exam.duration_minutes} minutes
                        </div>
                        {exam.start_datetime && (
                          <div className="flex items-center gap-2 text-indigo-600">
                            <Calendar className="w-4 h-4" />
                            Starts: {new Date(exam.start_datetime).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={() => updateExamStatus(exam.id, 'active')}
                        className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Activate Exam
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Active Exams Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                Active Exams ({exams.filter(e => e.status === 'active').length})
              </h3>
              {exams.filter(e => e.status === 'active').length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
                  No active exams
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exams.filter(e => e.status === 'active').map((exam) => (
                    <div key={exam.id} className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-slate-900">{exam.title}</h4>
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                          Active
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {exam.board_name && (
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                            {exam.board_name}
                          </span>
                        )}
                        {exam.grade_name && (
                          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                            {exam.grade_name}
                          </span>
                        )}
                        {exam.subject_name && (
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                            {exam.subject_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm text-slate-600 space-y-1 mb-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {exam.questions.length} questions
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {exam.duration_minutes} minutes
                        </div>
                      </div>
                      
                      <button
                        onClick={() => updateExamStatus(exam.id, 'closed')}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all"
                      >
                        Close Exam
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                Create New {createUserType === 'teacher' ? 'Teacher' : createUserType === 'student' ? 'Student' : 'Data Entry Operator'}
              </h2>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {createUserType === 'student' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                    <select
                      value={formData.board_id}
                      onChange={(e) => setFormData({ ...formData, board_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Board</option>
                      {boards.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                    <select
                      value={formData.grade_id}
                      onChange={(e) => setFormData({ ...formData, grade_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Grade</option>
                      {grades.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {createUserType !== 'data_entry_operator' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subjects</label>
                  <div className="flex flex-wrap gap-2">
                    {subjects.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => handleSubjectToggle(s.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          formData.subject_ids.includes(s.id)
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Edit User: {editingUser.name}</h2>
              <p className="text-sm text-slate-500">{editingUser.email}</p>
            </div>
            
            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {editingUser.role === 'student' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                    <select
                      value={editFormData.board_id}
                      onChange={(e) => setEditFormData({ ...editFormData, board_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Board</option>
                      {boards.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                    <select
                      value={editFormData.grade_id}
                      onChange={(e) => setEditFormData({ ...editFormData, grade_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Grade</option>
                      {grades.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {editingUser.role !== 'data_entry_operator' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subjects</label>
                  <div className="flex flex-wrap gap-2">
                    {subjects.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => handleSubjectToggle(s.id, true)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          editFormData.subject_ids.includes(s.id)
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Reset Password</h2>
              <p className="text-sm text-slate-500 mt-1">
                Set a new password for <span className="font-medium text-slate-700">{resetPasswordUser.name}</span>
              </p>
            </div>
            
            <form onSubmit={handleResetPassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowResetPasswordModal(false); setResetPasswordUser(null); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-all"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* School Settings Modal */}
      {showSchoolSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">School Settings</h2>
            </div>
            
            <form onSubmit={handleUpdateSchoolSettings} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">School Name</label>
                <input
                  type="text"
                  required
                  value={schoolSettings.name}
                  onChange={(e) => setSchoolSettings({ ...schoolSettings, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Logo URL</label>
                <input
                  type="url"
                  value={schoolSettings.logo_url}
                  onChange={(e) => setSchoolSettings({ ...schoolSettings, logo_url: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://example.com/logo.png"
                />
                {schoolSettings.logo_url && (
                  <div className="mt-2">
                    <img src={schoolSettings.logo_url} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSchoolSettingsModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolAdminDashboard;
